import { Button, Input, message, Popconfirm, Space, Table, Image, Select, Flex } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { CheckCircleOutlined, CheckSquareOutlined, CloseOutlined, PlusOutlined, StopOutlined } from '@ant-design/icons';
import CreateModelpack from './CreateModelpack';
import EditModelpack from './EditModelpack';
import ApproveModelpack from './ApproveModelpack';
import { Filter } from '../../../types/types';
import { GenerateODataFilter } from '../../../helpers/odataFunctions';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { useDebounce } from 'use-debounce';
import { AssetApprovalStatus, AssetApprovalStatusLabels, AssetApprovalStatusOptions } from '../../../models/enums';
import { BASE_PATH } from '../../..';
import { keepPreviousData } from '@tanstack/react-query';
import { MetadataSelector } from '../Inputs/MetadataSelector';
import { ModelPackViewDTO } from '../../../backendapi/requests';
import { useModelPacksServiceDeleteBackendModelPacks, useModelPacksServiceGetBackendModelPacks } from '../../../backendapi/queries';
import ReactTimeago from 'react-timeago';


const ModelpackList: React.FC = () => {
    const [messageApi, contextHolder] = message.useMessage();
    const [selectedModelpack, setSelectedModelpack] = useState<ModelPackViewDTO | undefined>(undefined);

    const [approveOpen, setApproveOpen] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);

    const [query, setQuery] = useState("");
    const [debouncedQuery] = useDebounce(query, 500);
    const [statuses, setStatuses] = useState<number[]>([]);
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);
    const [sortField, setSortField] = useState<string>("id");
    const [sortOrder, setSortOrder] = useState<"desc" | "asc">("desc");
    const [customFilters, setCustomFilters] = useState<Filter[]>([]);


    const filters = useMemo(() => {
        let filters: Filter[] = [];

        if (debouncedQuery) {
            filters.push({ type: 'search', values: [{value: debouncedQuery}], property: 'title', name: 'title' });
        }

        if (statuses.length > 0) {
            filters.push({ type: 'select', values: statuses.map(e => ({value: e})), property: 'approvalStatus', name: 'approvalStatus' });
        }

        for (let i = 0; i < customFilters.length; i++) {
            if(customFilters[i].values.length > 0){
                filters.push(customFilters[i]);
            }
        }

        return filters;
    }, [debouncedQuery, statuses, customFilters]);

    useEffect(() => {
        setPage(1);
    }, [debouncedQuery, statuses, customFilters]);

    const { data, isFetching, refetch } = useModelPacksServiceGetBackendModelPacks({
        top: pageSize.toString(),
        skip: ((page - 1) * pageSize).toString(),
        filter: GenerateODataFilter(filters),
        orderby: `${sortField} ${sortOrder}`,
        count: 'true'
    }, undefined, { refetchOnWindowFocus: false, placeholderData: keepPreviousData });
    const { mutate, isSuccess } = useModelPacksServiceDeleteBackendModelPacks();

    const handleCustomFilterAdd = useCallback(() => {
        setCustomFilters(v => [...v, { type: 'select', values: [], property: '', name: '' }]);
    }, []);
    const handleCustomFilterRemove = useCallback((index: number) => {
        setCustomFilters(v => v.filter((e,i) => i !== index));
    }, []);
    const handleCustomFilterChange = useCallback((value: Filter, index: number) => {
        setCustomFilters(v => v.map((e,i) => i === index ? value : e));
    }, []);


    const handleStatusChange = useCallback((value: any, option: { label: string; value: string; } | { label: string; value: string; }[]) => {
        if (Array.isArray(option)) {
            setStatuses(option.map(e => parseInt(e.value)));
        }
    }, []);

    const openCreate = () => {
        setCreateOpen(true);
    };

    const handleCreateClose = useCallback(() => {
        setCreateOpen(false);
        refetch();
    }, [refetch]);

    const handleEditClose = useCallback(() => {
        setSelectedModelpack(undefined);
        refetch();
    }, [refetch]);

    const startApproval = () => {
        setApproveOpen(true);
    };

    const handleApprovalClose = useCallback(() => {
        setApproveOpen(false);
        refetch();
    }, [refetch]);

    const handleTableChange = useCallback((pagination: TablePaginationConfig, filters: Record<string, FilterValue | null>, sorter: SorterResult<ModelPackViewDTO> | SorterResult<ModelPackViewDTO>[]) => {
        setPage(pagination.current ?? 1);
        setPageSize(pagination.pageSize ?? 10);

        if (!Array.isArray(sorter)) {
            setSortField(sorter.field?.toString() ?? "id");
            setSortOrder(sorter.order === 'ascend' ? 'asc' : 'desc');
        }
    }, []);

    const handleDelete = useCallback((material: ModelPackViewDTO) => {
        if (material.id) {
            mutate({ key: material.id })
        }
    }, [mutate]);

    useEffect(() => {
        if (isSuccess) {
            messageApi.success("model pack deleted");
            refetch();
        }
    }, [isSuccess, messageApi, refetch]);

    const columns: ColumnsType<ModelPackViewDTO> = [
        {
            title: 'Id',
            dataIndex: 'id',
            key: 'id',
            sorter: (a, b) => (a.id || 0) - (b.id || 0),
        },
        {
            title: 'Thumbnail',
            dataIndex: 'thumbnail',
            key: 'title',
            render: (_, value) => (
                <Image
                    preview={false}
                    src={BASE_PATH + value.thumbnail}
                    height={100}
                />)
        },
        {
            title: 'Status',
            dataIndex: 'approvalStatus',
            key: 'approvalStatus',
            render: (_, value) => (AssetApprovalStatusOptions[value.approvalStatus as AssetApprovalStatus])
        },
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            sorter: (a, b) => (a.title || '').localeCompare(b.title || ''),
        },
        {
            title: 'Enabled',
            dataIndex: 'enabled',
            key: 'enabled',
            render: (_, value) => (value.isEnabled ? <CheckCircleOutlined style={{ color: '#389e0d' }} /> : <StopOutlined style={{ color: '#cf1322' }} />)
        },
        {
            title: 'Data import',
            dataIndex: 'timestampMetadataImport',
            key: 'timestampMetadataImport',
            render: (_, value) => (value.timestampMetadataImport ? <ReactTimeago date={value.timestampMetadataImport} /> : null),
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Space size="middle">
                    <Button type="link" onClick={() => setSelectedModelpack(record)}>Edit</Button>
                    <Popconfirm title="Sure to delete?" onConfirm={() => handleDelete(record)}>
                        <Button type="link">Delete</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <>
            {contextHolder}

            <Flex justify='space-between' style={{ marginBottom: 16 }}>

                <Space>
                    <Input placeholder='search' value={query} onChange={e => setQuery(e.target.value)} />
                    <Select
                        mode="tags"
                        placeholder="Status"
                        onChange={handleStatusChange}
                        popupMatchSelectWidth={false}
                        size='middle'
                        style={{ minWidth: 100 }}
                        options={[
                            {
                                label: AssetApprovalStatusLabels[AssetApprovalStatus.Quote],
                                value: AssetApprovalStatus.Quote.toString()
                            },
                            {
                                label: AssetApprovalStatusLabels[AssetApprovalStatus.Pending],
                                value: AssetApprovalStatus.Pending.toString()
                            },
                            {
                                label: AssetApprovalStatusLabels[AssetApprovalStatus.InProduction],
                                value: AssetApprovalStatus.InProduction.toString()
                            },
                            {
                                label: AssetApprovalStatusLabels[AssetApprovalStatus.Commited],
                                value: AssetApprovalStatus.Commited.toString()
                            },
                            {
                                label: AssetApprovalStatusLabels[AssetApprovalStatus.New],
                                value: AssetApprovalStatus.New.toString()
                            },
                            {
                                label: AssetApprovalStatusLabels[AssetApprovalStatus.Rejected],
                                value: AssetApprovalStatus.Rejected.toString()
                            },
                            {
                                label: AssetApprovalStatusLabels[AssetApprovalStatus.RejectedButModified],
                                value: AssetApprovalStatus.RejectedButModified.toString()
                            },
                            {
                                label: AssetApprovalStatusLabels[AssetApprovalStatus.Approved],
                                value: AssetApprovalStatus.Approved.toString()
                            },
                            {
                                label: AssetApprovalStatusLabels[AssetApprovalStatus.ApprovedButModified],
                                value: AssetApprovalStatus.ApprovedButModified.toString()
                            }
                        ]}
                    />
                </Space>

                <Space>
                    <Button type="primary" onClick={startApproval} icon={<CheckSquareOutlined />}>
                        Start approving
                    </Button>
                    <Button type="primary" onClick={openCreate} icon={<PlusOutlined />}>
                        New modelpack
                    </Button>
                </Space>
            </Flex>

            <Space direction='vertical'>
                {customFilters.map((e,i) => <Space key={e.property}><MetadataSelector assetType='modelpack' value={e} onChange={v => handleCustomFilterChange(v, i)} /><Button onClick={() => handleCustomFilterRemove(i)} icon={<CloseOutlined />}></Button></Space>)}
                <Button onClick={handleCustomFilterAdd} icon={<PlusOutlined />}>Filter</Button>
            </Space>


            <CreateModelpack isOpen={createOpen} onClose={handleCreateClose} />

            <EditModelpack isOpen={selectedModelpack != null} onClose={handleEditClose} modelpackId={selectedModelpack?.id} />

            <Table
                loading={isFetching}
                dataSource={data?.value ?? Array.from({ length: pageSize }).map(x => ({}))}
                columns={columns}
                rowKey={"id"}
                onChange={handleTableChange}
                showSorterTooltip={false}
                pagination={{
                    total: (data !== undefined && data['@odata.count']) ? data['@odata.count'] : 0,
                    current: page,
                    pageSize: pageSize,
                    pageSizeOptions: [10, 20, 50, 100],
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                    showQuickJumper: true,
                    showSizeChanger: true,
                }} />

            <ApproveModelpack isOpen={approveOpen} modelpacks={data?.value || []} onClose={handleApprovalClose} />
        </>
    );
};

export default ModelpackList;